import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, Button, PageHeader } from '@airclou/ui';
import type { Tab } from '@airclou/ui';
import { Plus } from 'lucide-react';
import { useRoutedTabs } from '@/hooks/useRoutedTabs';
import { MembersPage } from '../MembersPage';
import { InvitationsPage } from '../team/InvitationsPage';
import { InviteTeamMemberModal } from './InviteTeamMemberModal';
import { AccessRequestsTab } from './AccessRequestsTab';
import { useAuth } from '@/contexts/AuthContext';

export function PeoplePage() {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  // Access requests are answered by the organization's owner and admins only
  // (the API refuses everyone else), so only they see the tab.
  const canAnswerRequests = user?.orgRole === 'owner' || user?.orgRole === 'admin';
  const { activeTab, handleTabChange } = useRoutedTabs({
    basePath: '/people',
    validTabs: canAnswerRequests ? ['all', 'invited', 'requests'] : ['all', 'invited'],
    defaultTab: 'all'
  });

  const [showInviteModal, setShowInviteModal] = useState(false);
  // Only the active tab is mounted, so a successful invite refreshes whichever
  // list is on screen by bumping a key both tabs watch.
  const [refreshKey, setRefreshKey] = useState(0);

  const tabs: Tab[] = [
    {
      id: 'all',
      label: t('people.all'),
      content: (
        <MembersPage
          hideHeader
          onInviteClick={() => setShowInviteModal(true)}
          refreshKey={refreshKey}
        />
      ),
    },
    {
      id: 'invited',
      label: t('people.invited'),
      content: <InvitationsPage hideHeader refreshKey={refreshKey} />,
    },
    ...(canAnswerRequests
      ? [{ id: 'requests', label: t('people.requests'), content: <AccessRequestsTab /> }]
      : []),
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('people.title')}
        description={t('people.subtitle')}
        actions={
          <Button onClick={() => setShowInviteModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('people.inviteMember')}
          </Button>
        }
      />

      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Rendered by the page, not by a tab: the Invite button lives in the page
          header, which is visible on every tab, while `Tabs` mounts only the
          active tab's content. */}
      <InviteTeamMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvited={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
}
